import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Link as MuiLink,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ChangelogCategory,
  ChangelogEntry,
  ChangelogProduct,
  loadAllEntries,
} from '../utils/parseChangelog';
import { renderChangelogMarkdown } from '../utils/renderChangelogMarkdown';
import ChangelogVideo from '../components/ChangelogVideo';
import ChangelogVideoUploader from '../components/ChangelogVideoUploader';

const IS_BROKER_ADMIN = import.meta.env.VITE_IS_BROKER_ADMIN === 'true';
const LAST_SEEN_KEY = 'admin-whats-new-last-seen';

// Vite glob: pulls every .md under changelog/entries at build time.
// Path is relative to THIS file.
const ENTRY_MODULES = import.meta.glob<string>('../../changelog/entries/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

const CATEGORY_LABELS: Record<ChangelogCategory, string> = {
  feature: 'New',
  improvement: 'Improved',
  fix: 'Fixed',
  removed: 'Removed',
  infra: 'Behind the scenes',
};

const CATEGORY_COLORS: Record<
  ChangelogCategory,
  'success' | 'primary' | 'warning' | 'error' | 'secondary'
> = {
  feature: 'success',
  improvement: 'primary',
  fix: 'warning',
  removed: 'error',
  infra: 'secondary',
};

const PRODUCT_LABELS: Record<ChangelogProduct, string> = {
  'legal-opinion': 'Intelligent Automation',
  admin: 'Admin',
  'ai-scribe-frontend': 'AI-Scribe',
  'ai-scribe-admin': 'AI-Scribe Admin',
  'raf-frontend': 'RAF',
  'broker-ai': 'Broker AI',
  all: 'All products',
};

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function entryMatchesProduct(entry: ChangelogEntry, product: ChangelogProduct | 'all-filter'): boolean {
  if (product === 'all-filter') return true;
  if (entry.frontmatter.products.includes('all')) return true;
  return entry.frontmatter.products.includes(product);
}

const WhatsNewPage: React.FC = () => {
  const allEntries = useMemo(() => loadAllEntries(ENTRY_MODULES), []);

  const [categoryFilter, setCategoryFilter] = useState<ChangelogCategory | 'all'>('all');
  const [productFilter, setProductFilter] = useState<ChangelogProduct | 'all-filter'>(
    IS_BROKER_ADMIN ? 'broker-ai' : 'all-filter'
  );

  const [lastSeenAtMount] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_SEEN_KEY);
    } catch {
      return null;
    }
  });

  // Mark "now" as the last-seen timestamp once the page has been on screen.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
      } catch {
        /* localStorage unavailable — ignore */
      }
    }, 1500);
    return () => window.clearTimeout(timer);
  }, []);

  const filteredEntries = useMemo(
    () =>
      allEntries.filter((entry) => {
        if (categoryFilter !== 'all' && entry.frontmatter.category !== categoryFilter) return false;
        if (!entryMatchesProduct(entry, productFilter)) return false;
        return true;
      }),
    [allEntries, categoryFilter, productFilter]
  );

  const newSinceLastSeen = useMemo(() => {
    if (!lastSeenAtMount) return filteredEntries.length;
    return filteredEntries.filter((e) => new Date(e.frontmatter.date) > new Date(lastSeenAtMount)).length;
  }, [filteredEntries, lastSeenAtMount]);

  const productOptions: Array<ChangelogProduct | 'all-filter'> = IS_BROKER_ADMIN
    ? ['broker-ai', 'all-filter']
    : ['all-filter', 'legal-opinion', 'admin', 'ai-scribe-frontend', 'ai-scribe-admin', 'broker-ai', 'raf-frontend'];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>
          What's New
        </Typography>
        {newSinceLastSeen > 0 ? (
          <Badge
            badgeContent={newSinceLastSeen}
            color="primary"
            sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
          >
            <Chip label="new since last visit" color="primary" size="small" variant="outlined" />
          </Badge>
        ) : null}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Recent product changes you might want to mention to customers, in plain language. The "Behind the
        scenes" chip flags work that doesn't change the UI but keeps the platform healthy — useful when sales
        wants to show the breadth of work shipping each week.
      </Typography>

      {!IS_BROKER_ADMIN && <ChangelogVideoUploader />}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Category
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={categoryFilter}
            exclusive
            onChange={(_, val) => val && setCategoryFilter(val)}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="feature">New</ToggleButton>
            <ToggleButton value="improvement">Improved</ToggleButton>
            <ToggleButton value="fix">Fixed</ToggleButton>
            <ToggleButton value="removed">Removed</ToggleButton>
            <ToggleButton value="infra">Behind the scenes</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {!IS_BROKER_ADMIN && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Product
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={productFilter}
              exclusive
              onChange={(_, val) => val && setProductFilter(val)}
            >
              {productOptions.map((p) => (
                <ToggleButton key={p} value={p}>
                  {p === 'all-filter' ? 'All' : PRODUCT_LABELS[p as ChangelogProduct]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
      </Stack>

      {filteredEntries.length === 0 ? (
        <Alert severity="info">
          No entries match the current filters. Adjust the filters above, or check back later — new updates appear
          here as engineering ships them.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {filteredEntries.map((entry) => {
            const isNew =
              !!lastSeenAtMount &&
              new Date(entry.frontmatter.date) > new Date(lastSeenAtMount);
            return (
              <Card
                key={entry.slug}
                variant="outlined"
                sx={{
                  borderLeft: 4,
                  borderLeftColor: isNew ? 'primary.main' : 'divider',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      size="small"
                      label={CATEGORY_LABELS[entry.frontmatter.category]}
                      color={CATEGORY_COLORS[entry.frontmatter.category]}
                    />
                    {entry.frontmatter.products.map((p) => (
                      <Chip key={p} size="small" variant="outlined" label={PRODUCT_LABELS[p]} />
                    ))}
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(entry.frontmatter.date)}
                    </Typography>
                    {isNew ? (
                      <Chip size="small" label="New" color="primary" variant="filled" />
                    ) : null}
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {entry.frontmatter.title}
                  </Typography>

                  {/*
                    Above the body on purpose — the clip is the faster way to
                    understand the change, so a reader can watch it and skip the
                    prose. Renders nothing at all when there is no clip, so
                    entries without one are byte-identical to before.
                  */}
                  <ChangelogVideo
                    video={entry.frontmatter.video}
                    videoExpires={entry.frontmatter.videoExpires}
                  />

                  {entry.body ? (
                    <>
                      <Divider sx={{ my: 1 }} />
                      {renderChangelogMarkdown(entry.body)}
                    </>
                  ) : null}

                  {entry.frontmatter.trello ? (
                    <Box sx={{ mt: 1.5 }}>
                      <MuiLink
                        href={entry.frontmatter.trello}
                        target="_blank"
                        rel="noreferrer noopener"
                        variant="caption"
                      >
                        View ticket
                      </MuiLink>
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default WhatsNewPage;
